import { CONFIG } from "../config.ts";
import { clamp, damp } from "../math.ts";
import { STAGES, FINAL_STAGE, xpToNext, stageAt } from "../evolution.ts";

export class Player {
  x: number;
  y: number;
  vx = 0;
  vy = 0;
  stage = 0;
  xp = 0;
  /** Facing direction, smoothed, for the eyes/look. */
  faceX = 0;
  faceY = 1;
  /** Squash factor for juice; springs back to 1. */
  squash = 1;
  animT = 0;

  constructor(x: number, y: number) {
    this.x = x;
    this.y = y;
  }

  get radius(): number {
    return CONFIG.player.baseRadius * stageAt(this.stage).scale;
  }

  get speed(): number {
    const s = CONFIG.player.baseSpeed + CONFIG.player.speedPerStage * this.stage;
    return Math.max(CONFIG.player.minSpeed, s);
  }

  get xpNeeded(): number {
    return xpToNext(this.stage);
  }

  get isEnlightened(): boolean {
    return this.stage >= FINAL_STAGE;
  }

  update(dt: number, dirX: number, dirY: number, speedMul = 1) {
    this.animT += dt;

    const desiredVx = dirX * this.speed * speedMul;
    const desiredVy = dirY * this.speed * speedMul;
    this.vx = damp(this.vx, desiredVx, CONFIG.player.accel, dt);
    this.vy = damp(this.vy, desiredVy, CONFIG.player.accel, dt);

    this.x += this.vx * dt;
    this.y += this.vy * dt;

    // clamp to world with a little padding
    const r = this.radius;
    this.x = clamp(this.x, r, CONFIG.world.width - r);
    this.y = clamp(this.y, r, CONFIG.world.height - r);

    // smooth facing toward velocity when moving
    const sp = Math.hypot(this.vx, this.vy);
    if (sp > 8) {
      this.faceX = damp(this.faceX, this.vx / sp, 8, dt);
      this.faceY = damp(this.faceY, this.vy / sp, 8, dt);
    }

    // squash springs back to 1
    this.squash = damp(this.squash, 1, 10, dt);
  }

  /**
   * Add XP; returns the number of times the player evolved this call
   * (usually 0 or 1, but a big golden burst can chain).
   */
  addXp(amount: number): number {
    this.xp += amount;
    this.squash = 1.18; // little pop on eat
    let evolves = 0;
    while (!this.isEnlightened && this.xp >= this.xpNeeded) {
      this.xp -= this.xpNeeded;
      this.stage++;
      evolves++;
      if (this.isEnlightened) {
        this.xp = 0;
        break;
      }
    }
    return evolves;
  }

  get stageName(): string {
    return STAGES[Math.min(this.stage, FINAL_STAGE)].name;
  }
}
